export const ROLE = {
  PLATFORM_ADMIN: "platform_admin",
  PLATFORM_USER: "platform_user",
  SUPERVISOR: "supervisor",
  ACCOUNTANT: "accountant",
  USER: "user",
};

export const PLATFORM_ROLES = [ROLE.PLATFORM_ADMIN, ROLE.PLATFORM_USER];
export const GLOBAL_PANEL_ROLES = [...PLATFORM_ROLES];
export const ADMINISTRATIVE_ROLES = [
  ...PLATFORM_ROLES,
  ROLE.SUPERVISOR,
];
export const ACCOUNTING_ROLES = [
  ...PLATFORM_ROLES,
  ROLE.SUPERVISOR,
  ROLE.ACCOUNTANT,
];
export const REPORTING_ROLES = [...ACCOUNTING_ROLES];
export const AGENCY_SCOPED_ROLES = [
  ROLE.SUPERVISOR,
  ROLE.ACCOUNTANT,
  ROLE.USER,
];
export const FUND_REQUEST_ROLES = [ROLE.PLATFORM_ADMIN, ROLE.SUPERVISOR];

const ROLE_LABELS = {
  [ROLE.PLATFORM_ADMIN]: "Platform Admin",
  [ROLE.PLATFORM_USER]: "Platform User",
  [ROLE.SUPERVISOR]: "Supervisor",
  [ROLE.ACCOUNTANT]: "Accountant",
  [ROLE.USER]: "User",
};

export const isPlatformRole = (role) => PLATFORM_ROLES.includes(role);

export const hasGlobalPlatformAccess = (role) => isPlatformRole(role);

export const hasAdministrativeAccess = (role) =>
  ADMINISTRATIVE_ROLES.includes(role);

export const hasAccountingAccess = (role) => ACCOUNTING_ROLES.includes(role);

export const hasReportingAccess = (role) => REPORTING_ROLES.includes(role);

export const getRoleLabel = (role, t) => {
  if (!role) {
    return "-";
  }

  return t
    ? t(`roles.${role}`, { defaultValue: ROLE_LABELS[role] || role })
    : ROLE_LABELS[role] || role;
};

export const getScopeLabel = (scopeType, t) => {
  if (scopeType === "platform") {
    return t ? t("scopes.platform") : "Platform";
  }

  if (scopeType === "agency") {
    return t ? t("scopes.agency") : "Agency";
  }

  return "-";
};

export const getOrganizationScopeType = (owner = {}) => {
  if (owner?.platform?._id || owner?.platform?.name) {
    return "platform";
  }

  if (owner?.agency?._id || owner?.agency?.name) {
    return "agency";
  }

  return "";
};

export const getOrganizationName = (owner = {}) =>
  owner?.platform?.name || owner?.agency?.name || "-";

export const getOrganizationDisplay = (owner = {}, t) => {
  const scopeType = getOrganizationScopeType(owner);
  const organizationName = getOrganizationName(owner);

  if (!scopeType || organizationName === "-") {
    return "-";
  }

  return `${getScopeLabel(scopeType, t)}: ${organizationName}`;
};

export const getAssignableRoleOptions = ({ requesterRole, scopeType, t }) => {
  if (scopeType === "platform") {
    if (requesterRole === ROLE.PLATFORM_USER) {
      return [
        {
          value: ROLE.PLATFORM_USER,
          label: getRoleLabel(ROLE.PLATFORM_USER, t),
        },
      ];
    }

    if (hasGlobalPlatformAccess(requesterRole)) {
      return [
        {
          value: ROLE.PLATFORM_ADMIN,
          label: getRoleLabel(ROLE.PLATFORM_ADMIN, t),
        },
        {
          value: ROLE.PLATFORM_USER,
          label: getRoleLabel(ROLE.PLATFORM_USER, t),
        },
      ];
    }

    return [];
  }

  if (scopeType === "agency") {
    if (requesterRole === ROLE.SUPERVISOR) {
      return [
        {
          value: ROLE.SUPERVISOR,
          label: getRoleLabel(ROLE.SUPERVISOR, t),
        },
        {
          value: ROLE.ACCOUNTANT,
          label: getRoleLabel(ROLE.ACCOUNTANT, t),
        },
        {
          value: ROLE.USER,
          label: getRoleLabel(ROLE.USER, t),
        },
      ];
    }

    if (hasGlobalPlatformAccess(requesterRole)) {
      return [
        {
          value: ROLE.SUPERVISOR,
          label: getRoleLabel(ROLE.SUPERVISOR, t),
        },
        {
          value: ROLE.ACCOUNTANT,
          label: getRoleLabel(ROLE.ACCOUNTANT, t),
        },
        {
          value: ROLE.USER,
          label: getRoleLabel(ROLE.USER, t),
        },
      ];
    }
  }

  return [];
};

export const getRoleFilterOptions = (requesterRole, t) => {
  const scopeOptions = [
    ...getAssignableRoleOptions({
      requesterRole,
      scopeType: "agency",
      t,
    }),
    ...getAssignableRoleOptions({
      requesterRole,
      scopeType: "platform",
      t,
    }),
  ];

  return scopeOptions.filter(
    (option, index, collection) =>
      collection.findIndex((item) => item.value === option.value) === index,
  );
};
